import { useState } from "react";
import { useTranslation } from "react-i18next";
import { submitSignup } from "../api/billing";
import { useApiError } from "../hooks/useApiError";
import { NeuButton, NeuCard } from "../components/neu/Neu";
import { SignupDone, SignupHeader } from "../features/billing/SignupHeader";
import { ConsentBox } from "../features/billing/ConsentBox";
import { LEGAL_VERSION } from "../features/legal/content";
import { SignupFields } from "../features/billing/SignupFields";
import { PHONE_LEN } from "../features/billing/phoneLengths";

const EMPTY = { company_name: "", contact_name: "", contact_email: "",
                country_code: "AE", country: "الإمارات العربية المتحدة",
                phone_code: "+971", phone_local: "", employee_count: "" };

export default function CompanySignupPage() {
  const { t, i18n } = useTranslation();
  const describe = useApiError();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState({});

  const agreed = ["terms", "privacy", "eula"].every((k) => consent[k]);
  const expectedLen = PHONE_LEN[form.country_code] || 9;
  const phoneOk = !!form.country_code && (form.phone_local || "").length === expectedLen;

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) return setError(t("legal.mustAgree"));
    if (!phoneOk) return setError(t("signup.phoneInvalid"));
    setBusy(true);
    setError("");
    try {
      await submitSignup({
        ...form,
        phone: form.phone_code && form.phone_local ? `${form.phone_code}${form.phone_local}` : "",
        employee_count: Number(form.employee_count),
        language: i18n.language,
        accepted_terms: true,
        terms_version: LEGAL_VERSION,
      });
      setDone(true);
    } catch (err) {
      const code = err?.response?.data?.detail;
      setError(
        code === "request_already_pending" ? t("signup.request_already_pending") :
        (code?.code || code) === "email_exists" ? t("signup.emailInUseHint") :
        describe(err)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background px-5 py-10">
      <div className="mx-auto max-w-3xl">
        <SignupHeader />

        {done ? (
          <SignupDone />
        ) : (
          <NeuCard className="animate-rise-in" data-testid="signup-card">
            <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
              <SignupFields form={form} setForm={setForm} />
              <div className="sm:col-span-2">
                <p className="rounded-2xl bg-background p-4 text-xs text-muted-foreground shadow-neu-in"
                   data-testid="signup-store-note">{t("signup.storeNote")}</p>
              </div>
              <div className="sm:col-span-2">
                <ConsentBox value={consent} onChange={setConsent} />
              </div>
              {error ? (
                <p className="text-sm font-semibold text-destructive sm:col-span-2" data-testid="signup-error">{error}</p>
              ) : null}
              <NeuButton variant="primary" type="submit" data-testid="signup-submit"
                         disabled={busy || !agreed || !phoneOk} className="w-full py-4 sm:col-span-2">
                {busy ? t("app.loading") : t("signup.submit")}
              </NeuButton>
            </form>
          </NeuCard>
        )}
      </div>
    </div>
  );
}
